import React, { Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { useAuth } from '../../contexts/AuthContext';
import { useInvestorPreview } from '../../contexts/InvestorPreviewContext';
import { lazyWithRetry } from '../../lib/lazyWithRetry';

// ============================================================================
// Lazy-loaded admin pages (Phase 3)
// ============================================================================

const AdminDashboardPage = lazyWithRetry(
  () => import('../../pages/InvestorRelations/Admin/AdminDashboardPage')
);
const CapTablePage = lazyWithRetry(
  () => import('../../pages/InvestorRelations/Admin/CapTablePage')
);
const InvestorDetailPage = lazyWithRetry(
  () => import('../../pages/InvestorRelations/Admin/InvestorDetailPage')
);
const InvestorDirectoryPage = lazyWithRetry(
  () => import('../../pages/InvestorRelations/Admin/InvestorDirectoryPage')
);

// ============================================================================
// Lazy-loaded investor pages (Phase 7)
// ============================================================================

const InvestorDashboardPage = lazyWithRetry(
  () => import('../../pages/InvestorRelations/Investor/InvestorDashboardPage')
);
const InvestorProfilePage = lazyWithRetry(
  () => import('../../pages/InvestorRelations/Investor/InvestorProfilePage')
);

// ============================================================================
// Lazy-loaded document management pages (Phase 4)
// ============================================================================

const LibraryManagerPage = lazyWithRetry(
  () => import('../../pages/InvestorRelations/Admin/LibraryManagerPage')
);
const SharedLibraryPage = lazyWithRetry(
  () => import('../../pages/InvestorRelations/Investor/SharedLibraryPage')
);
const MyDocumentsPage = lazyWithRetry(
  () => import('../../pages/InvestorRelations/Investor/MyDocumentsPage')
);

// ============================================================================
// Lazy-loaded updates pages (Phase 5 + P18 timeline)
// ============================================================================

const UpdatesManagerPage = React.lazy(
  () => import('../../pages/InvestorRelations/Admin/UpdatesManagerPage')
);
const InvestorTimelinePage = React.lazy(
  () => import('../../pages/InvestorRelations/Investor/InvestorTimelinePage')
);
const UpdateDetailPage = React.lazy(
  () => import('../../pages/InvestorRelations/Investor/UpdateDetailPage')
);

// ============================================================================
// Lazy-loaded consent pages (Phase 6)
// ============================================================================

const ConsentManagerPage = React.lazy(
  () => import('../../pages/InvestorRelations/Admin/ConsentManagerPage').then(
    (m) => ({ default: m.ConsentManagerPage })
  )
);
const MyConsentsPage = React.lazy(
  () => import('../../pages/InvestorRelations/Investor/MyConsentsPage').then(
    (m) => ({ default: m.MyConsentsPage })
  )
);
const ConsentDetailPage = React.lazy(
  () => import('../../pages/InvestorRelations/Investor/ConsentDetailPage').then(
    (m) => ({ default: m.ConsentDetailPage })
  )
);

// ============================================================================
// Loading fallback for Suspense
// ============================================================================

function PageSkeleton() {
  return (
    <div className="min-h-screen bg-slate-50 animate-pulse">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="h-8 bg-slate-200 rounded w-1/3 mb-4" />
        <div className="h-4 bg-slate-100 rounded w-1/4 mb-8" />
        <div className="grid grid-cols-4 gap-4 mb-8">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-28 bg-slate-100 rounded-2xl" />
          ))}
        </div>
        <div className="h-64 bg-slate-100 rounded-2xl" />
      </div>
    </div>
  );
}

/**
 * Index route dispatcher for /investors.
 *
 * Admins/owners land on the admin interface (directory, cap table, library,
 * timeline). Pure investor users see their personal portal. Previously the
 * index hard-rendered the investor dashboard for both, so Owner users — who
 * usually have no personal investor record — hit the empty "No investment
 * records found" state instead of the admin tools.
 *
 * The exception is a portal preview: there the admin is deliberately looking
 * at an investor's view, so the index renders the dashboard.
 */
function InvestorRelationsHome() {
  const { isAdmin } = useAuth();
  const { isPreviewing } = useInvestorPreview();

  // While previewing, /investors is the investor's dashboard — bouncing the
  // admin to the admin dashboard would make the portal's home page the one
  // page a preview could not show.
  if (isAdmin() && !isPreviewing) {
    return <Navigate to="/investors/admin" replace />;
  }
  return (
    <Suspense fallback={<PageSkeleton />}>
      <InvestorDashboardPage />
    </Suspense>
  );
}

// ============================================================================
// Route definitions
// ============================================================================


export const routes: RouteObject[] = [
  // Admin routes (requireAdmin) — Phase 3: real pages
  {
    path: 'admin',
    element: (
      <ProtectedRoute requireAdmin>
        <Suspense fallback={<PageSkeleton />}>
          <AdminDashboardPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: 'admin/cap-table',
    element: (
      <ProtectedRoute requireAdmin>
        <Suspense fallback={<PageSkeleton />}>
          <CapTablePage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: 'admin/cap-table/:investorId',
    element: (
      <ProtectedRoute requireAdmin>
        <Suspense fallback={<PageSkeleton />}>
          <InvestorDetailPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: 'admin/investors',
    element: (
      <ProtectedRoute requireAdmin>
        <Suspense fallback={<PageSkeleton />}>
          <InvestorDirectoryPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: 'admin/library',
    element: (
      <ProtectedRoute requireAdmin>
        <Suspense fallback={<PageSkeleton />}>
          <LibraryManagerPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: 'admin/updates',
    element: (
      <ProtectedRoute requireAdmin>
        <Suspense fallback={<PageSkeleton />}>
          <UpdatesManagerPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: 'admin/consents',
    element: (
      <ProtectedRoute requireAdmin>
        <Suspense fallback={<PageSkeleton />}>
          <ConsentManagerPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },

  // Index route dispatches by role: admins -> admin dashboard, investors -> portal
  {
    index: true,
    element: (
      <ProtectedRoute>
        <InvestorRelationsHome />
      </ProtectedRoute>
    ),
  },
  {
    path: 'library',
    element: (
      <ProtectedRoute requiredRole={['Investor', 'Owner']}>
        <Suspense fallback={<PageSkeleton />}>
          <SharedLibraryPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: 'updates',
    element: (
      <ProtectedRoute requiredRole={['Investor', 'Owner']}>
        <Suspense fallback={<PageSkeleton />}>
          <InvestorTimelinePage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: 'updates/:updateId',
    element: (
      <ProtectedRoute requiredRole={['Investor', 'Owner']}>
        <Suspense fallback={<PageSkeleton />}>
          <UpdateDetailPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: 'consents',
    element: (
      <ProtectedRoute requiredRole={['Investor', 'Owner']}>
        <Suspense fallback={<PageSkeleton />}>
          <MyConsentsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: 'consents/:consentId',
    element: (
      <ProtectedRoute requiredRole={['Investor', 'Owner']}>
        <Suspense fallback={<PageSkeleton />}>
          <ConsentDetailPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: 'documents',
    element: (
      <ProtectedRoute requiredRole={['Investor', 'Owner']}>
        <Suspense fallback={<PageSkeleton />}>
          <MyDocumentsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: 'profile',
    element: (
      <ProtectedRoute requiredRole={['Investor', 'Owner']}>
        <Suspense fallback={<PageSkeleton />}>
          <InvestorProfilePage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
];
